import Content, { ContentNS } from "content/content";
import OutlinedButton from "features/shared/components/buttons/OutlinedButton.styled";
import SubmitButton from "features/shared/components/buttons/submit-button/SubmitButton";
import SelectFileButton from "features/shared/components/select-file-button/SelectFileButton";
import Body from "features/shared/styles/typography/Body.styled";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import { FC } from "react";
import { useLocation } from "react-router-dom";
import { useAppStore } from "store/store";

import FilesList from "./components/files-list/FilesList";
import useFileList from "./hooks/useFileList";
import useSubmitFiles from "./hooks/useSubmitFiles";
import StyledDocumentUpload from "./styles/StyledDocumentUpload.styled";
import useUploadAnalytics from "features/shared/hooks/analytics/useUploadAnalytics";

interface NavigationState {
  itemType: string;
  processingMayTakeLong: boolean | null;
  inputFile?: File | undefined;
  additionalPaxSessionIds?: string[];
  pageIndexToProcess?: number | null;
}

const DocumentUpload: FC = () => {
  useAppStore((state) => state.locale);

  const {
    itemType,
    processingMayTakeLong,
    inputFile,
    additionalPaxSessionIds,
    pageIndexToProcess,
  } = useLocation().state as NavigationState;

  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const [files, addFileToList, removeFileFromList] = useFileList(inputFile);

  const submitFiles = useSubmitFiles(
    itemType,
    files,
    processingMayTakeLong,
    additionalPaxSessionIds,
    pageIndexToProcess ?? undefined
  );

  const bothDocumentSidesPresent = files.length === 2;
  const showSecondSidePrompt = files.length === 1;

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFileToList(e);
    e.target.value = "";
  };

  const uploadButtonTitle =
    files.length === 0
      ? textUI("select-first-button")
      : textUI("select-second-button");

  useUploadAnalytics(itemType);

  if (bothDocumentSidesPresent) {
    return (
      <StyledDocumentUpload>
        <Summary>{textUI("submit-header")}</Summary>

        <Body className="main-prompt">
          {textUI("two-sided-document-prompt")}
        </Body>

        <FilesList files={files} removeFileFromList={removeFileFromList} />

        <SubmitButton onClick={submitFiles}>
          {textUI("submit-both-sides")}
        </SubmitButton>
      </StyledDocumentUpload>
    );
  }

  return (
    <StyledDocumentUpload>
      <Summary>{textUI("submit-header")}</Summary>

      <Body className="main-prompt">{textUI("two-sided-document-prompt")}</Body>

      <FilesList files={files} removeFileFromList={removeFileFromList} />

      {showSecondSidePrompt ? (
        <Body className="second-side-prompt">
          {textUI("please-upload-second")}
        </Body>
      ) : null}

      <SelectFileButton
        label={uploadButtonTitle}
        onChange={onFileSelect}
        primary={true}
      />

      {showSecondSidePrompt ? (
        <OutlinedButton onClick={submitFiles}>
          {textUI("submit-both-sides")}
        </OutlinedButton>
      ) : null}
    </StyledDocumentUpload>
  );
};

export default DocumentUpload;
